import { DateTime } from "luxon";

/**
 * Appends 'Z' to naive datetime strings so they are treated as UTC by Date().
 * Strings that already carry timezone info (Z, +, or offset -) are unchanged.
 * Date-only strings (YYYY-MM-DD) are passed through unchanged.
 */
const normalizeToUtc = (ts: string): string => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(ts)) return ts;
  return ts.includes("Z") || ts.includes("+") || ts.includes("-", 10) ? ts : `${ts}Z`;
};

/**
 * Converts a timezone-ambiguous timestamp to human-readable format
 * Assumes UTC for timestamps without timezone information
 *
 * @param timestamp - ISO-like timestamp (e.g., "2025-09-15T23:00:41.786226")
 * @param locale - Locale for formatting (defaults to 'en-US')
 * @param timeZone - Target timezone (defaults to user's local timezone)
 * @returns Formatted date string like "September 15, 2025"
 */
export const convertTimestampToDate = (
  timestamp: string | null | undefined,
  locale = "en-US",
  timeZone?: string,
): string => {
  if (!timestamp) {
    return "N/A";
  }

  try {
    const date = new Date(normalizeToUtc(timestamp));

    if (isNaN(date.getTime())) {
      throw new Error("Invalid date");
    }

    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: timeZone || undefined,
    };

    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch (error) {
    return "Invalid Date";
  }
};

/**
 * Formats a date string using short month, day, and year format
 *
 * @param date - ISO date string
 * @returns Formatted date string like "Dec 3, 2025" or empty string if no date provided
 */
export const formatDate = (date?: string | null): string => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * Formats a date string with context-aware date label and time.
 * Returns "Today • 3:30 PM" when the date is today,
 * or "Mon, Feb 2, 2026 • 2:30 PM" for any other date.
 *
 * @param date - ISO date string
 * @returns Formatted string or empty string if no date provided
 */
export const formatDateTime = (date?: string | null): string => {
  if (!date) return "";

  const d = new Date(date);
  const now = new Date();
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();

  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  if (isToday) return `Today • ${time}`;

  const datePart = d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return `${datePart} • ${time}`;
};

/**
 * Formats a date string as a long date label with an isToday flag.
 * Used for date group headers that need to differentiate today from other dates.
 *
 * @param date - ISO date string
 * @returns Object with isToday flag and formatted longDate string, or null on invalid input
 */
export const formatDateLabel = (
  date: string,
): { isToday: boolean; longDate: string } | null => {
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return null;
    const isToday = d.toDateString() === new Date().toDateString();
    const longDate = d.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return { isToday, longDate };
  } catch {
    return null;
  }
};

/**
 * Adds specified days to a given date and returns formatted date string
 * Returns undefined if no date is provided
 *
 * @param isoDate - ISO date string
 * @param daysToAdd - Number of days to add to the date (defaults to 0)
 * @returns Formatted date string or undefined
 */
export const addDays = (isoDate?: string | null, daysToAdd = 0): string | undefined => {
  if (!isoDate) return undefined;
  const date = DateTime.fromISO(isoDate).plus({ days: daysToAdd });
  return formatDate(date.toISO());
};

/**
 * Returns a local date key string (YYYY-MM-DD) from an ISO timestamp.
 * Used to group events by calendar day in the user's local timezone.
 */
export const getLocalDateKey = (isoString: string): string => {
  try {
    const d = new Date(isoString);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch {
    return isoString;
  }
};

export const parseLocalDate = (dateOnlyStr: string): Date =>
  new Date(`${dateOnlyStr}T00:00:00`);

/**
 * Returns days from today to given date. Negative = past, positive = future.
 * Handles both YYYY-MM-DD and full ISO strings, using local midnight for date-only.
 */
export const daysFromToday = (dateStr: string): number => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
  const target = isDateOnly ? parseLocalDate(dateStr) : new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

/** Formats a UTC datetime string as a context-aware local date + time label. */
export const formatDateTimeUtc = (date?: string | null): string =>
  formatDateTime(date ? normalizeToUtc(date) : date);

/** Formats a UTC datetime string as a long date label with an isToday flag. */
export const formatDateLabelUtc = (date: string) => formatDateLabel(normalizeToUtc(date));

/** Returns a local YYYY-MM-DD date key from a UTC datetime string. */
export const getLocalDateKeyUtc = (isoString: string): string =>
  getLocalDateKey(normalizeToUtc(isoString));

/** Formats a UTC datetime string as a short date, returning "—" for non-string values. */
export const formatDateWithDefaultUtc = (value: unknown): string =>
  typeof value === "string" ? formatDate(normalizeToUtc(value)) || "—" : "—";
